import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IIssueAssignee, IUser } from '@d-orca/shared/trusstor';
import { SelectButton } from './SelectButton';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import styles from './AssigneeSelector.module.css';

interface IAssigneeSelectorProps {
  value: IIssueAssignee | null;
  onChange: (v: IIssueAssignee | null) => void;
  options: readonly IUser[];
  compact?: boolean;
  selectStyle?: React.CSSProperties;
}

export function AssigneeSelector({ value, onChange, options }: IAssigneeSelectorProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const displayText = value ? (value.name ?? value.username) : null;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={containerRef} className={styles.container}>
      <SelectButton
        value={displayText}
        placeholder={t('issues.field.assigneePlaceholder')}
        showClear={!!value}
        onClear={() => onChange(null)}
        onClick={() => setOpen((v) => !v)}
      />

      {open && (
        <div className={styles.dropdown}>
          <ScrollArea viewportClassName={styles.dropdownBody}>
            <button
              type="button"
              className={styles.dropdownItem}
              onClick={() => { onChange(null); setOpen(false); }}
            >
              <span className={styles.dropdownItemText}>{t('issues.field.assigneeUnassigned')}</span>
            </button>
            {options.map((u) => (
              <button
                key={u.username}
                type="button"
                className={styles.dropdownItem}
                onClick={() => {
                  onChange({ username: u.username, name: u.name ?? u.username });
                  setOpen(false);
                }}
              >
                <span className={styles.dropdownItemText}>{u.name ?? u.username}</span>
              </button>
            ))}
          </ScrollArea>
        </div>
      )}
    </div>
  );
}
